/**
 * <s-promo-popup> — timed email sign-up pop-up.
 *
 * - First visit: opens after `data-delay` seconds.
 * - Closed without signing up: opens once more `data-reshow` minutes later
 *   (on the same page if the visitor is still there, or on a later page view).
 * - Signed up (from this pop-up or any other newsletter form): never shows
 *   again. After a pop-up sign-up the page reloads and it reopens with the code.
 * - In the theme editor it only opens while its block is selected.
 */
const STORAGE_KEY = 'freeflo-promo-popup';
const SUBMIT_FLAG = 'freeflo-promo-popup-submitted';

const read = () => {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)) || {}; } catch { return {}; }
};
const write = (state) => {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(state)); } catch { /* storage blocked */ }
};

class SPromoPopup extends HTMLElement {
  #dialog;
  #timer;
  #ac;

  connectedCallback() {
    this.#dialog = this.querySelector('dialog');
    if (!this.#dialog) return;
    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    this.querySelector('form')?.addEventListener('submit', () => {
      try { sessionStorage.setItem(SUBMIT_FLAG, '1'); } catch { /* storage blocked */ }
    }, opts);

    this.querySelector('[data-promo-copy]')?.addEventListener('click', (e) => this.#copy(e.currentTarget), opts);

    this.#dialog.addEventListener('close', () => this.#onClose(), opts);

    if (window.Shopify?.designMode) {
      document.addEventListener('shopify:block:select', (e) => {
        if (this.contains(e.target) || e.target === this) this.#open();
      }, opts);
      document.addEventListener('shopify:block:deselect', (e) => {
        if ((this.contains(e.target) || e.target === this) && this.#dialog.open) this.#dialog.close();
      }, opts);
      return;
    }

    this.#init();
  }

  disconnectedCallback() {
    clearTimeout(this.#timer);
    this.#ac?.abort();
  }

  #init() {
    const params = new URLSearchParams(location.search);
    let fromPopup = false;
    try {
      fromPopup = sessionStorage.getItem(SUBMIT_FLAG) === '1';
      sessionStorage.removeItem(SUBMIT_FLAG);
    } catch { /* storage blocked */ }

    if (params.get('customer_posted') === 'true') {
      write({ status: 'subscribed', at: Date.now() });
      if (fromPopup) this.#open('success');
      return;
    }

    // A pop-up submission that came back with an error (e.g. invalid email): show it again.
    if (fromPopup && this.querySelector('input[aria-invalid="true"]')) {
      this.#open();
      return;
    }

    const state = read();
    if (state.status === 'subscribed' || state.status === 'done') return;

    const delay = Math.max(0, Number(this.dataset.delay) || 0) * 1000;

    if (state.status === 'dismissed') {
      const reshow = Math.max(1, Number(this.dataset.reshow) || 8) * 60000;
      const wait = Math.max(delay, state.at + reshow - Date.now());
      this.#schedule(wait, 'reshown');
      return;
    }

    this.#schedule(delay, 'shown');
  }

  #schedule(ms, status) {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      // Don't interrupt the cart, menu or search; try again shortly.
      if (document.querySelector('dialog[open]') || document.visibilityState !== 'visible') {
        this.#schedule(4000, status);
        return;
      }
      this.#pending = status;
      this.#open();
    }, ms);
  }

  #pending = null;

  #open(mode) {
    if (this.#dialog.open) return;
    if (mode === 'success') {
      this.#dialog.dataset.state = 'success';
      this.#dialog.setAttribute('aria-labelledby', 'promo-popup-success-title');
    }
    this.#dialog.showModal();
    if (mode === 'success') this.querySelector('[data-promo-success-title]')?.focus();
  }

  #onClose() {
    if (window.Shopify?.designMode) return;
    if (this.#dialog.dataset.state === 'success') return;
    const state = read();
    if (state.status === 'subscribed') return;
    // First close starts the reshow timer; closing the reshown pop-up ends it for good.
    if (this.#pending === 'reshown' || state.status === 'dismissed') {
      write({ status: 'done', at: Date.now() });
      return;
    }
    const next = { status: 'dismissed', at: Date.now() };
    write(next);
    const reshow = Math.max(1, Number(this.dataset.reshow) || 8) * 60000;
    this.#schedule(reshow, 'reshown');
  }

  async #copy(button) {
    const code = this.querySelector('[data-promo-code]')?.textContent.trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      const original = button.textContent;
      button.textContent = button.dataset.copiedLabel || original;
      setTimeout(() => { button.textContent = original; }, 2000);
    } catch { /* clipboard unavailable; code stays visible to copy manually */ }
  }
}

if (!customElements.get('s-promo-popup')) customElements.define('s-promo-popup', SPromoPopup);
